import React, { useState, useMemo, useCallback } from 'react';
import { formatDateOnlyUTC } from '../../../utils/dateOnly';
import { FiSearch, FiChevronUp, FiChevronDown, FiCheck } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Invoice } from '../../../hooks/useInvoicesInfinite';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { InvoiceActions } from './InvoiceActions';
import { BatchCheckbox } from '../../../components/BatchCheckbox';
import { InlineBatchActions } from '../../../components/InlineBatchActions';
import { useBatchSelection } from '../../../contexts/BatchSelectionContext';
import { useAuth } from '../../../contexts/AuthContext';

interface InvoiceTableProps {
  invoices: Invoice[];
  loading?: boolean;
  onEdit?: (invoiceId: string) => void;
  onDelete?: (invoiceId: string) => void;
  onView?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string) => void;
  onSend?: (invoiceId: string) => void;
  onDuplicate?: (invoiceId: string) => void;
  className?: string;
  searchTerm?: string;
  onSearchTermChange?: (term: string) => void;
  statusFilter?: string;
  onStatusFilterChange?: (filter: string) => void;
}

type SortField = 'number' | 'customer' | 'issue_date' | 'due_date' | 'before_tax' | 'status' | 'total';
type SortDirection = 'asc' | 'desc';

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  invoices,
  loading = false,
  onEdit,
  onDelete,
  onView,
  onDownload,
  onSend,
  onDuplicate,
  className = '',
  searchTerm: propSearchTerm,
  onSearchTermChange,
  statusFilter: propStatusFilter,
  onStatusFilterChange
}) => {
  const [sortField, setSortField] = useState<SortField>('number');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalStatusFilter, setInternalStatusFilter] = useState<string>('all');
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  
  // Use props if provided, otherwise use internal state
  const searchTerm = propSearchTerm !== undefined ? propSearchTerm : internalSearchTerm;
  const statusFilter = propStatusFilter !== undefined ? propStatusFilter : internalStatusFilter;
  
  // Memoize the search term setter to prevent unnecessary re-renders
  const setSearchTerm = useCallback((term: string) => {
    if (onSearchTermChange) {
      onSearchTermChange(term);
    } else {
      setInternalSearchTerm(term);
    }
  }, [onSearchTermChange]);
  
  const setStatusFilter = useCallback((filter: string) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(filter);
    } else {
      setInternalStatusFilter(filter);
    }
  }, [onStatusFilterChange]);

  // Helper function to get the correct total amount (including tax)
  function getInvoiceTotal(invoice: Invoice): number {
    if (invoice.total_amount !== null && invoice.total_amount !== undefined) {
      return Number(invoice.total_amount);
    }
    if (
      invoice.sub_total !== null &&
      invoice.sub_total !== undefined &&
      invoice.tax_total !== null &&
      invoice.tax_total !== undefined
    ) {
      return Number(invoice.sub_total) + Number(invoice.tax_total);
    }
    return 0;
  }

  function getInvoiceBeforeTaxTotal(invoice: Invoice): number {
    if (invoice.sub_total !== null && invoice.sub_total !== undefined) {
      return Number(invoice.sub_total);
    }
    if (
      invoice.total_amount !== null &&
      invoice.total_amount !== undefined &&
      invoice.tax_total !== null &&
      invoice.tax_total !== undefined
    ) {
      return Number(invoice.total_amount) - Number(invoice.tax_total);
    }
    return getInvoiceTotal(invoice);
  }

  // Memoize filtered and sorted invoices to prevent unnecessary re-renders
  const filteredAndSortedInvoices = useMemo(() => {
    let filtered = [...invoices];
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(invoice => invoice.status === statusFilter);
    }
    
    // Apply search filter (client-side filtering is now handled in the hook)
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(invoice => {
        const name = (invoice.display_name || `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || invoice.company_name || '').toLowerCase();
        const addr = `${invoice.billing_addr_line1 || ''} ${invoice.billing_addr_city || ''} ${invoice.billing_addr_state || ''} ${invoice.billing_addr_zip || ''}`.toLowerCase();
        return (
          invoice.number.toLowerCase().includes(q) ||
          name.includes(q) ||
          addr.includes(q)
        );
      });
    }
    
    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'number':
          aValue = a.number;
          bValue = b.number;
          break;
        case 'customer':
          aValue = (a.display_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.company_name || '').toLowerCase();
          bValue = (b.display_name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.company_name || '').toLowerCase();
          break;
        case 'issue_date':
          aValue = new Date(a.issue_date).getTime();
          bValue = new Date(b.issue_date).getTime();
          break;
        case 'due_date':
          aValue = new Date(a.due_date).getTime();
          bValue = new Date(b.due_date).getTime();
          break;
        case 'before_tax':
          aValue = getInvoiceBeforeTaxTotal(a);
          bValue = getInvoiceBeforeTaxTotal(b);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'total':
          aValue = getInvoiceTotal(a);
          bValue = getInvoiceTotal(b);
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [invoices, searchTerm, statusFilter, sortField, sortDirection]);

  const {
    selectedItems,
    selectAll,
    deselectAll,
    getSelectedCount,
    setOrderedIds
  } = useBatchSelection();

  // Keep ordered ids in sync for shift-click
  React.useEffect(() => {
    setOrderedIds(filteredAndSortedInvoices.map(inv => inv.id));
  }, [filteredAndSortedInvoices, setOrderedIds]);

  React.useEffect(() => {
    let cancelled = false;

    const pruneSelection = async () => {
      if (selectedItems.size === 0 || !currentBusiness) {
        return;
      }

      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/invoices/ids`, {
          headers,
          credentials: 'include',
        });

        if (!response.ok) {
          return;
        }

        const validIds = await response.json();
        if (cancelled || !Array.isArray(validIds)) {
          return;
        }

        const validIdSet = new Set(validIds.map((id) => String(id)));
        const nextSelection = Array.from(selectedItems).filter((id) => validIdSet.has(String(id)));

        if (nextSelection.length === selectedItems.size) {
          return;
        }

        if (nextSelection.length === 0) {
          deselectAll();
          return;
        }

        selectAll(nextSelection);
      } catch (_) {}
    };

    void pruneSelection();

    return () => {
      cancelled = true;
    };
  }, [API_BASE_URL, currentBusiness, deselectAll, invoices, isCookieMode, selectAll, selectedItems, token]);

  // Handle select all functionality
  const handleSelectAll = useCallback(() => {
    const visibleInvoiceIds = filteredAndSortedInvoices.map(invoice => invoice.id);
    const allVisibleSelected = visibleInvoiceIds.every(id => selectedItems.has(id));
    
    if (allVisibleSelected) {
      deselectAll();
    } else {
      selectAll(visibleInvoiceIds);
    }
  }, [filteredAndSortedInvoices, selectedItems, selectAll, deselectAll]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FiChevronUp className="h-4 w-4" /> : <FiChevronDown className="h-4 w-4" />;
  }, [sortField, sortDirection]);

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'partial_open', label: 'Partial Open' },
    { value: 'partial_over', label: 'Partial Overdue' },
    { value: 'closed', label: 'Closed' }
  ];

  if (loading) {
    return (
      <div className={`rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900 ${className}`} data-testid="invoice-table">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
          {/* Additional skeleton polish */}
          <div className="px-4 sm:px-5 pb-5 mt-4">
            <ul className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <li key={i} className="h-10 rounded-lg bg-gray-100 animate-pulse dark:bg-neutral-800" />
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900 ${className}`} data-testid="invoice-table">
      {/* Filters */}
      <div className="border-b border-gray-200 p-4 dark:border-neutral-800">
        <div className="flex flex-col sm:flex-row gap-4 items-stretch">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by invoice #, name, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2 dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
            >
              {statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>


          </div>
        </div>
        
        {/* Selection Indicator (existing inline batch actions remain) */}
        {getSelectedCount() > 0 && (
          <div className="mt-3">
            <InlineBatchActions entityType="invoices" />
          </div>
        )}
      </div>

      {/* Table with sticky header and scroll container */}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-10 bg-white/95 backdrop-blur dark:bg-neutral-900/95">
            <tr>
              {/* Batch Selection Column */}
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <div className="flex items-center justify-center">
                  <div 
                    className={`
                      relative inline-flex items-center justify-center w-4 h-4 
                      border rounded cursor-pointer transition-colors
                      ${filteredAndSortedInvoices.length > 0 && 
                        filteredAndSortedInvoices.every(invoice => selectedItems.has(invoice.id))
                        ? 'bg-sedra border-sedra' 
                        : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
                      }
                      ${filteredAndSortedInvoices.length === 0 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'hover:border-sedra/60'
                      }
                    `}
                    onClick={handleSelectAll}
                  >
                    {filteredAndSortedInvoices.length > 0 && 
                     filteredAndSortedInvoices.every(invoice => selectedItems.has(invoice.id)) && (
                      <FiCheck className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('number')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Invoice #</span>
                  {getSortIcon('number')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('customer')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Customer</span>
                  {getSortIcon('customer')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('issue_date')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Issue Date</span>
                  {getSortIcon('issue_date')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('due_date')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Due Date</span>
                  {getSortIcon('due_date')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('before_tax')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Total Before Tax</span>
                  {getSortIcon('before_tax')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('total')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Total Due</span>
                  {getSortIcon('total')}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="group inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
                >
                  <span>Status</span>
                  {getSortIcon('status')}
                </button>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-neutral-800 dark:bg-neutral-900">
            {filteredAndSortedInvoices.map((invoice) => (
              <tr key={invoice.id} className="group transition-colors hover:bg-sedra/5 dark:hover:bg-sedra/10">
                {/* Batch Selection Checkbox */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center justify-center">
                    <BatchCheckbox 
                      id={invoice.id}
                      className="w-4 h-4"
                    />
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <Link 
                    to={`/invoices/${invoice.id}`} 
                    className="group inline-flex flex-col focus:outline-none"
                  >
                    <span className="text-sm font-semibold text-sedra transition-colors duration-200 group-hover:text-sedra-700 dark:text-sedra dark:group-hover:text-sedra-200">
                      #{invoice.number}
                    </span>
                    <span className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      View invoice
                    </span>
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <Link 
                    to={`/customers/${invoice.customer_id}`} 
                    className="group block focus:outline-none"
                  >
                    <div className="text-sm font-medium text-sedra transition-colors duration-200 group-hover:text-sedra-700 dark:text-sedra dark:group-hover:text-sedra-200">
                      {(() => {
                        const customerName = invoice.display_name || 
                          `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || 
                          invoice.company_name || 
                          'Unknown Customer';
                        return customerName;
                      })()}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                      {invoice.billing_addr_line1 || [invoice.billing_addr_city, invoice.billing_addr_state, invoice.billing_addr_zip].filter(Boolean).join(', ') || 'Customer details'}
                    </div>
                  </Link>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="text-sm text-gray-700 dark:text-gray-200">{formatDateOnlyUTC(invoice.issue_date)}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Issued</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="text-sm text-gray-700 dark:text-gray-200">{formatDateOnlyUTC(invoice.due_date)}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Due</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    ${getInvoiceBeforeTaxTotal(invoice).toFixed(2)}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    Before tax
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-base font-extrabold tabular-nums text-gray-900 dark:text-white">
                    ${getInvoiceTotal(invoice).toFixed(2)}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    Balance due: ${Number(invoice.balance_due || 0).toFixed(2)}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap align-top">
                  <InvoiceStatusBadge 
                    status={invoice.status} 
                    size="sm"
                    balanceDue={invoice.balance_due}
                    totalAmount={invoice.total_amount}
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium align-top">
                  <InvoiceActions
                    invoice={invoice}
                    onEdit={onEdit ? () => onEdit(invoice.id) : () => {}}
                    onDelete={onDelete ? () => onDelete(invoice.id) : () => {}}
                    onDuplicate={onDuplicate ? () => onDuplicate(invoice.id) : () => {}}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredAndSortedInvoices.length === 0 && (
        <div className="text-center py-12">
          <div className="mx-auto mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-sedra/10 text-sedra">✺</div>
          <div className="text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all' ? 'No invoices match your filters.' : 'No invoices found.'}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable; 
